/**
 * The reschedule commit, pure: the request a drag proposal becomes, the
 * optimistic move of its bar, and the dry-run -> commit/rollback sequence
 * around the PATCH. The network and the query cache come in as arguments, so
 * the whole sequence is testable without a DOM; the React side is in
 * `useReschedule.tsx` (the Planning's drag) and `MoveBooking.tsx` (the
 * booking page's "Sposta…", OPS-1: the same sequence, with no board to shift).
 */
import type { ApiProblem, ApiSchemas } from '@rental/contracts/client'
import type { DragProposal } from './timeline-drag'

type Board = ApiSchemas['TimelineResponse']
type Item = ApiSchemas['TimelineItem']
export type RescheduleBody = ApiSchemas['StaffRescheduleRequestInput']

/** The `timeline` message keys that explain a refused or failed move. */
export type RescheduleReason =
  | 'reasonVehicleTaken'
  | 'reasonCategory'
  | 'reasonNotReschedulable'
  | 'reasonDeliveryZone'
  | 'reasonOpeningHours'
  | 'moveFailed'

const REASONS: Readonly<Record<string, RescheduleReason>> = {
  vehicle_taken: 'reasonVehicleTaken',
  category: 'reasonCategory',
  not_reschedulable: 'reasonNotReschedulable',
  delivery_zone: 'reasonDeliveryZone',
  opening_hours: 'reasonOpeningHours',
}

/** The staff words for a refusal code; the server's own English is never shown. */
export function rescheduleReason(code: string): RescheduleReason {
  return REASONS[code] ?? 'moveFailed'
}

/** The PATCH body for a proposal, or null when it asks for nothing the API accepts. */
export function rescheduleBody(proposal: DragProposal): RescheduleBody | null {
  if (proposal.axis === 'vehicle') {
    return proposal.toVehicleId ? { vehicleId: proposal.toVehicleId } : null
  }
  if (proposal.axis === 'resize-start') return { startsAt: proposal.startsAt.toISOString() }
  if (proposal.axis === 'resize-end') return { endsAt: proposal.endsAt.toISOString() }
  return { startsAt: proposal.startsAt.toISOString(), endsAt: proposal.endsAt.toISOString() }
}

/** One call of PATCH /v1/staff/bookings/{code}/schedule, as the orchestration needs it. */
export type ReschedulePatch = (request: {
  code: string
  body: RescheduleBody
  dryRun: boolean
  key: string
}) => Promise<
  { ok: true; data: ApiSchemas['StaffRescheduleResponse'] } | { ok: false; problem: ApiProblem }
>

function reasonOf(problem: ApiProblem): RescheduleReason {
  return problem.kind === 'api' ? rescheduleReason(problem.code) : 'moveFailed'
}

export type ProposeOutcome = { kind: 'ok' } | { kind: 'refused'; reason: RescheduleReason }

/** A change to one booking's schedule, as the API takes it: from a drag, or typed (OPS-1). */
export interface ScheduleChange {
  code: string
  body: RescheduleBody
}

/** Asks the API whether the change would be accepted, changing nothing (`?dryRun=1`). */
export async function proposeChange(
  patch: ReschedulePatch,
  change: ScheduleChange,
  newKey: () => string,
): Promise<ProposeOutcome> {
  const result = await patch({ code: change.code, body: change.body, dryRun: true, key: newKey() })
  return result.ok ? { kind: 'ok' } : { kind: 'refused', reason: reasonOf(result.problem) }
}

/** The dry run of a drag's proposal. */
export async function proposeMove(
  patch: ReschedulePatch,
  proposal: DragProposal,
  newKey: () => string,
): Promise<ProposeOutcome> {
  const body = rescheduleBody(proposal)
  if (!body) return { kind: 'refused', reason: 'moveFailed' }
  return proposeChange(patch, { code: proposal.code, body }, newKey)
}

/** The booking's bar moved on a board: a new board, the old one untouched. */
export function applyMove(board: Board, proposal: DragProposal): Board {
  const shifted = (item: Item): Item => ({
    ...item,
    startsAt: proposal.startsAt.toISOString(),
    endsAt: proposal.endsAt.toISOString(),
  })
  const isBar = (item: Item) => item.booking?.code === proposal.code && item.kind === 'reservation'
  return {
    ...board,
    categories: board.categories.map((category) => {
      const moving = category.vehicles
        .flatMap((vehicle) => vehicle.items)
        .concat(category.unassigned)
        .find(isBar)
      if (!moving) return category
      const moved = shifted(moving)
      return {
        ...category,
        vehicles: category.vehicles.map((vehicle) => {
          const kept = vehicle.items.filter((item) => !isBar(item))
          if (proposal.axis !== 'vehicle') {
            return { ...vehicle, items: vehicle.items.map((i) => (isBar(i) ? moved : i)) }
          }
          return vehicle.id === proposal.toVehicleId
            ? { ...vehicle, items: [...kept, moved] }
            : { ...vehicle, items: kept }
        }),
        unassigned:
          proposal.axis !== 'vehicle'
            ? category.unassigned.map((i) => (isBar(i) ? moved : i))
            : category.unassigned.filter((item) => !isBar(item)),
      }
    }),
  }
}

/** What the commit needs of the query cache: read it, change it, put it back, refresh it. */
export interface BoardCache {
  snapshot: () => Board | undefined
  update: (change: (board: Board) => Board) => void
  restore: (snapshot: Board | undefined) => void
  invalidate: () => Promise<void>
}

export type CommitOutcome = { kind: 'moved' } | { kind: 'failed'; reason: RescheduleReason }

/**
 * Commits a confirmed change: with a board in the cache, its bar moves there
 * at once (`shift`) and a refusal or failure puts the board back; the real
 * PATCH goes out under its own key. Without a board (the booking page), only
 * the PATCH: the caller refreshes what it shows from the answer.
 */
export async function commitChange(
  patch: ReschedulePatch,
  cache: BoardCache | null,
  change: ScheduleChange,
  shift: ((board: Board) => Board) | null,
  newKey: () => string,
): Promise<CommitOutcome> {
  const before = cache?.snapshot()
  if (cache && shift) cache.update(shift)
  const result = await patch({ code: change.code, body: change.body, dryRun: false, key: newKey() })
  if (!result.ok) {
    if (cache) {
      cache.restore(before)
      await cache.invalidate()
    }
    return { kind: 'failed', reason: reasonOf(result.problem) }
  }
  await cache?.invalidate()
  return { kind: 'moved' }
}

/** The commit of a drag's proposal: the bar moves on the board at once. */
export async function commitMove(
  patch: ReschedulePatch,
  cache: BoardCache,
  proposal: DragProposal,
  newKey: () => string,
): Promise<CommitOutcome> {
  const body = rescheduleBody(proposal)
  if (!body) return { kind: 'failed', reason: 'moveFailed' }
  return commitChange(
    patch,
    cache,
    { code: proposal.code, body },
    (board) => applyMove(board, proposal),
    newKey,
  )
}
